"use client";
import React, { useState, useEffect, useRef } from "react";
import { getAvailableSlots } from "../utils/getAvailableSlots";
import { track } from "../lib/track";
import { v4 as uuidv4 } from "uuid";

export type ScheduleCallSubmitData = {
  date: string;
  startTime: string;
  slotLabel: string;
  slotRange: string;
  formValues: Record<string, string>;
  bookingId: string;
  isDiscovery: boolean;
};

interface ScheduleCallProps {
  duration: number;
  meetingType: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  isFree?: boolean;
  componentName?: string;
  referredBy?: string;
  preferredTimes?: string[];
  onSubmit?: (data: ScheduleCallSubmitData) => Promise<void>;
}

type DayStatus = "loading" | "available" | "none";

const toMonthStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;


const ScheduleCall: React.FC<ScheduleCallProps> = ({
  duration,
  meetingType,
  title,
  subtitle,
  ctaText,
  isFree,
  componentName = "ScheduleCall",
  referredBy,
  preferredTimes = ["10:00AM", "11:00AM", "12:00PM"],
  onSubmit,
}) => {
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [selectedSlotRaw, setSelectedSlotRaw] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [quickBookClicked, setQuickBookClicked] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);
  const scrollToFormRef = useRef(false);
  // Synchronous submit lock. The `isSubmitting` state guard alone can be bypassed
  // by a same-tick double-click: both handler invocations read the stale (false)
  // state from their render closure before React re-renders the disabled button.
  // A ref updates synchronously, so the second click is rejected immediately.
  const isSubmittingRef = useRef(false);

  const [formValues, setFormValues] = useState<Record<string, string>>({
    name: "", email: "", phone: "", dob: "",
  });

  // Per-day availability status — drives calendar cell rendering
  const [dayStatus, setDayStatus] = useState<Record<string, DayStatus>>({});
  // Slot cache: keyed by day, populated by individual day-click fetches
  const slotsCache = useRef<Record<string, string[]>>({});
  // Batch slot cache: populated from the single month-availability call, used only for quick-book
  const batchSlotsCache = useRef<Record<string, string[]>>({});
  // Carries a quick-book slot through the selectedDay effect so it isn't reset
  const pendingQuickSlotRef = useRef<string | null>(null);

  // Date bounds computed once at mount
  const bounds = useRef<{ min: Date; max: Date; currentMonth: string; maxMonth: string } | null>(null);
  if (!bounds.current) {
    const t = new Date();
    const min = new Date(t); min.setDate(t.getDate() + 2); min.setHours(0, 0, 0, 0);
    const max = new Date(t.getFullYear(), t.getMonth() + 6, t.getDate());
    bounds.current = {
      min, max,
      currentMonth: toMonthStr(min),
      maxMonth: toMonthStr(new Date(t.getFullYear(), t.getMonth() + 5, 1)),
    };
  }
  const { min: minDate, max: maxDate, currentMonth, maxMonth } = bounds.current;

  const [calendarMonth, setCalendarMonth] = useState(currentMonth);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const effectiveMeetingType = referredBy ? `${meetingType} - via ${referredBy}` : meetingType;
  const mt = (meetingType || "").trim().toLowerCase();
  const isDiscovery = typeof isFree === "boolean" ? isFree : mt.includes("discovery");

  const displayTitle = title || (
    isDiscovery ? "Wellness Discovery Call"
    : mt.includes("full-consultation") ? "Detailed Consultation"
    : mt.includes("flexible-consultation") || mt === "flexible" ? "Follow-up Consultation"
    : mt.includes("farleigh-method") ? "Farleigh Method Consultation"
    : mt.includes("bodhi-club") ? "Bodhi Club"
    : "Schedule a Call"
  );

  const displaySubtitle = typeof subtitle === "string" ? subtitle
    : isDiscovery
    ? "This is your moment to reconnect with your energy and wellbeing — choose a time that suits you best. No charge. No obligation. Just clarity."
    : "Choose a time that suits you best. We'll confirm by email and guide you through the next step.";

  const submitLabel = ctaText || (isDiscovery ? "Book My Call" : "Confirm & Book");

  // ——— Calendar helpers ———

  const isInDateRange = (day: string): boolean => {
    const d = new Date(day + "T00:00:00");
    const dow = d.getDay();
    return d >= minDate && d <= maxDate && dow !== 0 && dow !== 6;
  };

  const calendarMonthLabel = (() => {
    const [y, m] = calendarMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
  })();

  // ——— Single batched availability fetch for the displayed month + next month ———
  // Replaces the old per-day loop (which fired 43 simultaneous Graph calls and hit rate limits).
  // One POST to /api/graph/calendar/available-days returns available days + their slots.
  useEffect(() => {
    const toMark: string[] = [];
    const addMonthDays = (ym: string) => {
      const [year, month] = ym.split("-").map(Number);
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const day = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        if (isInDateRange(day)) toMark.push(day);
      }
    };
    addMonthDays(calendarMonth);
    const [y, m] = calendarMonth.split("-").map(Number);
    const nextMonth = toMonthStr(new Date(y, m, 1));
    if (nextMonth <= maxMonth) addMonthDays(nextMonth);

    if (toMark.length === 0) return;

    setDayStatus(prev => {
      const next = { ...prev };
      for (const day of toMark) next[day] = "loading";
      return next;
    });

    let cancelled = false;

    fetch("/api/graph/calendar/available-days", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ month: calendarMonth, duration }),
    })
      .then(r => r.json())
      .then(({ availableDays, slotsByDay }: { availableDays: string[]; slotsByDay: Record<string, string[]> }) => {
        if (cancelled) return;
        const available = new Set(availableDays ?? []);
        setDayStatus(prev => {
          const next = { ...prev };
          for (const day of toMark) next[day] = available.has(day) ? "available" : "none";
          return next;
        });
        if (slotsByDay) {
          for (const [day, slots] of Object.entries(slotsByDay)) {
            batchSlotsCache.current[day] = slots as string[];
          }
        }
        // If the displayed month has no available days, skip ahead to the next month
        const currentMonthDays = toMark.filter(d => d.startsWith(calendarMonth));
        const hasAvailableInCurrentMonth = currentMonthDays.some(d => available.has(d));
        if (!hasAvailableInCurrentMonth) {
          const [y, m] = calendarMonth.split("-").map(Number);
          const nextMo = toMonthStr(new Date(y, m, 1));
          if (nextMo <= maxMonth) setCalendarMonth(nextMo);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDayStatus(prev => {
          const next = { ...prev };
          for (const day of toMark) next[day] = "none";
          return next;
        });
      });

    return () => { cancelled = true; };
  }, [calendarMonth, duration]); // eslint-disable-line react-hooks/exhaustive-deps

  // ——— When a day is selected, use cached slots or fall back to a fresh fetch ———
  useEffect(() => {
    if (!selectedDay) return;
    setSelectedSlotRaw(null);
    setSelectedSlot(null);
    setSelectedStartTime(null);

    const pendingSlot = pendingQuickSlotRef.current;
    pendingQuickSlotRef.current = null;

    const applyPending = (slots: string[]) => {
      if (pendingSlot && slots.includes(pendingSlot)) applySlotSelection(pendingSlot);
    };

    const cached = slotsCache.current[selectedDay];
    if (cached !== undefined) {
      setAvailableSlots(cached);
      applyPending(cached);
      return;
    }

    const fetchAvailable = async () => {
      setIsLoadingSlots(true);
      try {
        const slots = await getAvailableSlots(selectedDay, duration);
        slotsCache.current[selectedDay] = slots;
        setAvailableSlots(slots);
        applyPending(slots);
      } catch (err) {
        console.error("Error fetching available slots:", err);
        setAvailableSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
    fetchAvailable();
  }, [selectedDay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSlot && scrollToFormRef.current) {
      scrollToFormRef.current = false;
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedSlot]);

  const hasTrackedView = useRef(false);
  useEffect(() => {
    const sessionKey = `tracked_scheduler_${meetingType}`;
    if (hasTrackedView.current || sessionStorage.getItem(sessionKey)) return;
    hasTrackedView.current = true;
    sessionStorage.setItem(sessionKey, "1");
    try { track("scheduler_view", { label: meetingType, component: componentName }, { event: "scheduler_view", label: meetingType, component: componentName }); } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(p => ({ ...p, [name]: value }));
  };

  // ——— Shared slot parsing — sets start/end time state, returns formatted range ———
  const applySlotSelection = (slot: string): string => {
    const [time, period] = slot.split(/(AM|PM)/i).map(p => p.trim());
    const [h, mStr] = time.split(":");
    const mins = parseInt(mStr, 10);
    let hour = parseInt(h, 10);
    if (/PM/i.test(period) && hour !== 12) hour += 12;
    else if (/AM/i.test(period) && hour === 12) hour = 0;
    const startH = hour.toString().padStart(2, "0");
    const startM = mins.toString().padStart(2, "0");
    setSelectedStartTime(`${startH}:${startM}`);
    const end = new Date(0);
    end.setHours(hour, mins + duration, 0, 0);
    const formatted = `${startH}:${startM} - ${end.getHours().toString().padStart(2, "0")}:${end.getMinutes().toString().padStart(2, "0")}`;
    setSelectedSlotRaw(slot);
    setSelectedSlot(formatted);
    setQuickBookClicked(null);
    return formatted;
  };

  const handleSlotSelect = (slot: string) => {
    scrollToFormRef.current = true;
    const formatted = applySlotSelection(slot);
    try { track("slot_selected", { label: `${selectedDay} ${slot} (${formatted})`, component: `${componentName} - ${meetingType}` }, { event: "slot_selected", label: `${selectedDay} ${slot} (${formatted})`, component: `${componentName} - ${meetingType}` }); } catch {}
  };

  // ——— Quick book: jump straight from pill → form ———
  const handleQuickBook = (day: string, slot: string) => {
    if (selectedDay === day && selectedSlotRaw === slot) return;
    if (quickBookClicked === `${day}|${slot}`) return;
    setQuickBookClicked(`${day}|${slot}`);
    pendingQuickSlotRef.current = slot;
    scrollToFormRef.current = true;
    setCalendarMonth(day.substring(0, 7));
    setSelectedDay(day);
    try { track("quick_book", { label: `${day} ${slot}`, component: componentName }, { event: "quick_book", label: `${day} ${slot}`, component: componentName }); } catch {}
  };

  async function handleDiscoverySubmit(bookingId: string) {
    const payload = { bookingId, date: selectedDay, startTime: selectedStartTime, duration, slotLabel: selectedSlotRaw, slotRange: selectedSlot, email: formValues.email || "N/A", name: formValues.name || "N/A", phone: formValues.phone || "N/A", meetingType: effectiveMeetingType, isFree: true, onlineMeeting: true };
    let response: Response;
    if (process.env.NEXT_PUBLIC_SPOOF_CALENDAR === "true") {
      response = { ok: true, status: 200, json: async () => ({ success: true, message: "Spoofed dev response.", bookingId, intakeToken: null }) } as Response;
    } else {
      response = await fetch("/api/graph/calendar/book", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) throw new Error(data?.message || "Calendar booking failed");
    setBookingSuccess(true);
    try { track("booking_confirmed", { label: `${meetingType} | ${formValues.email}`, component: componentName }, { event: "booking_confirmed", label: `${meetingType} | ${formValues.email}`, component: componentName, meta: { date: selectedDay, slot: selectedSlot, meetingType } }); } catch {}
    const destination = data.intakeToken ? `/workwithme/intake?token=${data.intakeToken}` : "/";
    setTimeout(() => { window.location.href = destination; }, 2000);
  }

  async function handlePaidSubmit() {
    const res = await fetch("/api/consultations/initiate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ meetingType: effectiveMeetingType, date: selectedDay, startTime: selectedStartTime, duration, name: formValues.name, email: formValues.email, phone: formValues.phone, dob: formValues.dob, formValues }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) throw new Error(data.error || "Failed to initiate booking");
    window.location.href = data.paymentUrl;
  }

  const handleSubmit = async () => {
    if (isSubmittingRef.current || isSubmitting) return;
    isSubmittingRef.current = true;
    setFormError(null);
    setIsSubmitting(true);
    try {
      if (!formValues.email || !formValues.email.includes("@")) { setFormError("Please provide a valid email address."); return; }
      if (!formValues.name?.trim()) { setFormError("Please provide your name."); return; }
      if (!selectedDay || !selectedSlot || !selectedSlotRaw || !selectedStartTime) { setFormError("Please select a date and time slot before submitting."); return; }
      const bookingId = uuidv4();
      try { track("schedule_submit", { label: `${meetingType} | ${formValues.email}`, component: componentName }, { event: "schedule_submit", label: `Submit ${meetingType} | ${formValues.email}`, component: componentName, meta: { date: selectedDay, slot: selectedSlot, title: displayTitle, free: isDiscovery } }); } catch {}
      if (onSubmit) {
        await onSubmit({ date: selectedDay!, startTime: selectedStartTime!, slotLabel: selectedSlotRaw!, slotRange: selectedSlot!, formValues, bookingId, isDiscovery });
      } else if (isDiscovery) {
        await handleDiscoverySubmit(bookingId);
      } else {
        await handlePaidSubmit();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormError(
        error instanceof Error && error.message
          ? `${error.message}. If this keeps happening your booking may already have gone through — please email us before trying again.`
          : "Something went wrong reaching our calendar. Please try again in a moment."
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ——— Derive quick-book options from cached availability ———
  // One option per available day (earliest preferred time: 10am → 11am → 12pm → first slot)
  // Up to 3 options from the earliest available days
  const quickBookOptions: { day: string; slot: string; dayLabel: string; timeLabel: string }[] = [];
  {
    const availableDays = Object.keys(dayStatus)
      .filter(d => dayStatus[d] === "available")
      .sort()
      .slice(0, 14);

    for (const day of availableDays) {
      const cached = batchSlotsCache.current[day] ?? slotsCache.current[day];
      if (!cached?.length) continue;
      const slot = preferredTimes.find(t => cached.includes(t)) ?? cached[0];
      const d = new Date(day + "T00:00:00");
      const dayLabel = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      const timeLabel = slot.replace(":00", "").toLowerCase(); // "10am", "11am", "12pm"
      quickBookOptions.push({ day, slot, dayLabel, timeLabel });
      if (quickBookOptions.length >= 3) break;
    }
  }

  // ——— Success screen ———
  if (bookingSuccess) {
    return (
      <div className="bg-gradient-to-br from-[var(--color-brand-hover)] via-white to-[var(--color-accent)] rounded-3xl border border-[var(--color-brand)]/10 shadow-xl p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--color-brand)] text-white text-3xl flex items-center justify-center mx-auto mb-5">✓</div>
        <h2 className="text-2xl font-semibold text-[var(--color-brand)] mb-2">You&apos;re booked in!</h2>
        <p className="text-gray-700">Keep an eye out for a calendar invite.</p>
        <p className="text-sm text-gray-400 mt-3">
          {isDiscovery ? "Setting up your health intake form…" : "Redirecting…"}
        </p>
      </div>
    );
  }

  const monthIsLoading = Object.values(dayStatus).some(s => s === "loading");

  const availableDaysInMonth = Object.keys(dayStatus)
    .filter(d => d.startsWith(calendarMonth) && dayStatus[d] === "available")
    .sort();

  // ——— UI ———
  return (
    <div className="relative">
      <div className="bg-gradient-to-br from-[var(--color-brand-hover)] via-white to-[var(--color-accent)] rounded-3xl border border-[var(--color-brand)]/10 shadow-xl p-6 md:p-10">

        <header className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-[var(--color-brand)]">
            {displayTitle.includes("Call") ? (
              <>{displayTitle.split(" Call")[0]}<span className="italic text-[var(--color-text-brand-highlight)]"> Call</span></>
            ) : displayTitle}
          </h1>
          <p className="mt-2 text-gray-600 text-sm md:text-base max-w-prose">{displaySubtitle}</p>
        </header>

        {/* ── Quick book strip: 3 next available slots ── */}
        {(monthIsLoading && quickBookOptions.length === 0) ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Next available</p>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-[58px] w-24 rounded-2xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          </div>
        ) : quickBookOptions.length > 0 ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Next available</p>
            <div className="flex flex-wrap gap-2">
              {quickBookOptions.map(({ day, slot, dayLabel, timeLabel }) => {
                const isActive = (selectedDay === day && selectedSlotRaw === slot) || quickBookClicked === `${day}|${slot}`;
                const isDisabled = quickBookClicked !== null && quickBookClicked !== `${day}|${slot}`;
                return (
                  <button
                    key={`${day}-${slot}`}
                    type="button"
                    onClick={() => handleQuickBook(day, slot)}
                    disabled={isDisabled}
                    className={[
                      "flex flex-col items-center px-4 py-2.5 rounded-2xl text-sm border transition-all",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
                      isDisabled
                        ? "bg-white border-[var(--color-brand)]/10 text-[var(--color-brand)]/30 cursor-not-allowed"
                        : isActive
                        ? "bg-[var(--color-brand)] border-[var(--color-brand)] text-white shadow-md"
                        : "bg-white border-[var(--color-brand)]/25 text-[var(--color-brand)] hover:border-[var(--color-brand)] hover:shadow-sm",
                    ].join(" ")}
                  >
                    <span className="font-semibold leading-tight">{timeLabel}</span>
                    <span className={["text-xs leading-tight mt-0.5", isActive ? "text-white/80" : "text-gray-500"].join(" ")}>{dayLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* ── Divider ── */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-[var(--color-brand)]/10" />
          <span className="text-xs text-gray-400 whitespace-nowrap">or choose a date</span>
          <div className="flex-1 h-px bg-[var(--color-brand)]/10" />
        </div>

        {/* ── Available dates for month ── */}
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4 mb-5">

          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => {
                const [y, m] = calendarMonth.split("-").map(Number);
                setCalendarMonth(toMonthStr(new Date(y, m - 2, 1)));
                setSelectedDay(null);
                setAvailableSlots([]);
              }}
              disabled={calendarMonth <= currentMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 text-xl leading-none disabled:opacity-20 hover:bg-gray-100 transition"
              aria-label="Previous month"
            >‹</button>

            <div className="text-center">
              <p className="text-sm font-semibold text-gray-800">{calendarMonthLabel}</p>
              {monthIsLoading && (
                <p className="text-[11px] text-gray-400 animate-pulse mt-0.5">Checking availability…</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                const [y, m] = calendarMonth.split("-").map(Number);
                setCalendarMonth(toMonthStr(new Date(y, m, 1)));
                setSelectedDay(null);
                setAvailableSlots([]);
              }}
              disabled={calendarMonth >= maxMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 text-xl leading-none disabled:opacity-20 hover:bg-gray-100 transition"
              aria-label="Next month"
            >›</button>
          </div>

          {/* Date pills */}
          {monthIsLoading ? (
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-9 w-20 rounded-full bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : availableDaysInMonth.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-2">No available dates this month — try the next month.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableDaysInMonth.map(day => {
                const d = new Date(day + "T00:00:00");
                const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                const isSelected = day === selectedDay;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setSelectedDay(day);
                      setAvailableSlots([]);
                      setSelectedSlotRaw(null);
                      setSelectedSlot(null);
                      setSelectedStartTime(null);
                      try { track("click", { label: `Day selected: ${day}`, component: `${componentName} - ${meetingType}` }, { event: "click", label: `Day selected: ${day}`, component: `${componentName} - ${meetingType}`, meta: { timestamp: Date.now() } }); } catch {}
                    }}
                    className={[
                      "px-3 py-1.5 rounded-full text-sm font-medium border transition-all",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
                      isSelected
                        ? "bg-[var(--color-brand)] border-[var(--color-brand)] text-white shadow-md"
                        : "bg-white border-[var(--color-brand)]/25 text-[var(--color-brand)] hover:border-[var(--color-brand)] hover:shadow-sm",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Time slots ── */}
        {selectedDay && (
          isLoadingSlots ? (
            <p className="text-sm text-[var(--color-brand)] animate-pulse mb-5">Loading times…</p>
          ) : availableSlots.length > 0 ? (
            <div className="mb-5">
              <p className="text-sm font-medium text-gray-700 mb-3">
                {new Date(selectedDay + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <div className="flex flex-wrap gap-2">
                {availableSlots.map(slot => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleSlotSelect(slot)}
                    aria-pressed={selectedSlotRaw === slot}
                    className={[
                      "px-4 py-2 rounded-full text-sm font-medium transition-all",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
                      selectedSlotRaw === slot
                        ? "bg-[var(--color-brand)] text-white shadow-md scale-105"
                        : "bg-white border border-[var(--color-brand)]/30 text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-white hover:scale-105",
                    ].join(" ")}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
              No times available on this day — please pick another date.
            </p>
          )
        )}

        {/* ── Contact details + submit ── */}
        {selectedSlot && (
          <div className="mt-5" ref={formRef}>
            <div className="rounded-2xl border border-gray-100 bg-white/80 p-4 md:p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">Your details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="sched-name">
                    Full name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="sched-name"
                    name="name"
                    type="text"
                    value={formValues.name}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="sched-email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="sched-email"
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange}
                    placeholder="jane@example.com"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="sched-phone">Phone</label>
                  <input
                    id="sched-phone"
                    name="phone"
                    type="tel"
                    value={formValues.phone}
                    onChange={handleChange}
                    placeholder="+44 7700 000000"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/40"
                  />
                </div>
                {!isDiscovery && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="sched-dob">Date of birth</label>
                    <input
                      id="sched-dob"
                      name="dob"
                      type="date"
                      value={formValues.dob}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/40"
                    />
                  </div>
                )}
              </div>
            </div>

            {formError && (
              <p role="alert" className="mt-3 text-sm text-red-600 text-center">{formError}</p>
            )}

            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); void handleSubmit(); }}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="mt-5 mx-auto flex items-center justify-center gap-2 px-8 py-3 bg-[var(--color-brand)] text-white rounded-full text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-black/20"
            >
              {isSubmitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
              )}
              {isSubmitting ? "Booking…" : submitLabel}
            </button>

            {isSubmitting && (
              <p className="mt-2 text-center text-xs text-gray-500">
                Contacting the calendar — this can take a few seconds. Please don&apos;t refresh or tap again.
              </p>
            )}

            <p className="mt-3 text-center text-xs text-gray-500">
              {isDiscovery
                ? "You'll receive a calendar invite by email. No charge, no obligation."
                : "You'll be taken to our secure payment page. A calendar invite and confirmation will follow once payment is complete."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleCall;
